import type {KeyboardEvent, MouseEventHandler, ReactNode} from 'react';

import {Box, SxProps, Theme, Typography} from '@mui/material';

import {greenColors, orangeColors} from '../../layouts/theme/designSystem';

export interface Props {
  title: string;
  value: number | string;
  subtitle?: ReactNode;
  fillWidth?: boolean;
  /** Stack title, value, and subtitle as a centered group (no footer-pinned subtitle). */
  contentLayout?: 'default' | 'centered';
  /** Emphasize title/value/subtitle (matches-page pending card styling). */
  highlight?: boolean;
  /** When the card is clickable, shows a solid orange border for the filter currently applied. */
  active?: boolean;
  onClick?: MouseEventHandler<HTMLDivElement>;
  valueSx?: SxProps<Theme>;
  fillHeight?: boolean;
}

export const BaseStatCard = ({
  title,
  value,
  subtitle,
  fillWidth,
  contentLayout = 'default',
  highlight,
  active,
  onClick,
  valueSx,
  fillHeight,
}: Props) => {
  const titleSubtitleColor = highlight ? orangeColors[900] : greenColors[700];
  const isClickable = Boolean(onClick);
  const isCentered = contentLayout === 'centered';

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.currentTarget.click();
    }
  };

  return (
    <Box
      onClick={onClick}
      onKeyDown={isClickable ? handleKeyDown : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      sx={{
        ...(fillWidth
          ? {width: '100%', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box'}
          : {
            flex: '1 1 0',
            minWidth: {xs: '100%', sm: '160px'},
            maxWidth: {sm: '210px'},
          }),
        bgcolor: 'white',
        border: active ? `2px solid ${orangeColors[70]}` : `1px solid ${orangeColors[3]}`,
        borderRadius: '15px',
        px: 2,
        py: 1.5,
        minHeight: 127,
        ...(fillHeight && {height: '100%'}),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: isCentered ? 'center' : subtitle ? 'flex-start' : 'center',
        gap: isCentered ? 0.5 : 0,
        textAlign: 'center',
        ...(isClickable && {
          cursor: 'pointer',
          transformOrigin: 'center center',
          transition: 'transform 0.2s ease, border 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'scale(1.03)',
          },
          '&:active': {
            transform: 'scale(1.01)',
          },
          '&:focus-visible': {
            outline: `2px solid ${orangeColors[100]}`,
            outlineOffset: 2,
          },
        }),
      }}
    >
      <Typography
        sx={{
          fontSize: '14px',
          lineHeight: 1.5,
          color: titleSubtitleColor,
          letterSpacing: '0.42px',
          mb: isCentered ? 0 : 0.5,
          fontWeight: highlight ? 600 : 400,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: '34px',
          lineHeight: 1.5,
          fontWeight: 700,
          my: isCentered ? 0 : 0.5,
          ...valueSx,
          ...(highlight ? {color: titleSubtitleColor} : {}),
        }}
      >
        {value}
      </Typography>
      {subtitle ? (
        <Typography
          sx={{
            fontSize: '14px',
            lineHeight: 1.5,
            color: titleSubtitleColor,
            letterSpacing: '0.42px',
            mt: isCentered ? 0 : 'auto',
            fontWeight: highlight ? 500 : 400,
          }}
        >
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
};
